import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, LayoutGrid } from 'lucide-react';
import { CATEGORIES } from '../../config/categories';
import CategoryIcon from '../directory/CategoryIcon';

/** All Services + one link per category, generated from the category config. */
function useDirectoryLinks() {
  const { t } = useTranslation();
  return [
    { to: '/directory', label: t('directory.allServices'), icon: <LayoutGrid className="w-4 h-4" aria-hidden /> },
    ...CATEGORIES.map((c) => ({
      to: `/directory/${c.slug}`,
      label: t(`directory.categoryPlural.${c.key}`),
      icon: <CategoryIcon category={c.key} className="w-4 h-4" />,
    })),
  ];
}

/** Desktop: "Directory" dropdown in the pill header. */
export function DirectoryDropdown() {
  const { t } = useTranslation();
  const location = useLocation();
  const links = useDirectoryLinks();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isActive = location.pathname.startsWith('/directory');

  // Close on route change, outside click and Escape.
  useEffect(() => setOpen(false), [location.pathname, location.search]);
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative shrink-0" ref={ref}>
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1 whitespace-nowrap text-sm py-3 border-b-2 transition-colors ${
          isActive ? 'text-ink font-semibold border-ink' : 'text-ash hover:text-ink border-transparent'
        }`}
      >
        {t('nav.directory')}
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {open && (
        <div className="absolute top-full mt-2 start-0 min-w-[13rem] rounded-[16px] bg-paper border border-ink/10 shadow-[0_4px_16px_rgba(0,0,0,.10)] py-2 z-50">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className={`flex items-center gap-3 px-4 min-h-11 text-sm hover:bg-fog transition-colors ${
                location.pathname === l.to ? 'text-ink font-semibold' : 'text-ink/80'
              }`}
            >
              {l.icon}
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/** Mobile: expandable "Directory" section in the slide-out menu. */
export function DirectoryMobileSection({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation();
  const location = useLocation();
  const links = useDirectoryLinks();
  const [expanded, setExpanded] = useState(location.pathname.startsWith('/directory'));

  return (
    <div>
      <button
        type="button"
        aria-expanded={expanded}
        onClick={() => setExpanded((e) => !e)}
        className="flex items-center justify-between w-full min-h-14 text-ink/70 hover:text-ink transition-colors"
      >
        {t('nav.directory')}
        <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} aria-hidden />
      </button>
      {expanded && (
        <div className="pb-2 ps-3">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={onNavigate}
              className={`flex items-center gap-3 min-h-11 ${
                location.pathname === l.to ? 'text-ink font-semibold' : 'text-ink/70 hover:text-ink'
              }`}
            >
              {l.icon}
              {l.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
